import type { OnChanges, SimpleChanges } from '@angular/core';
import { Component, EventEmitter, Input, Output } from '@angular/core';
import type { ButtonType } from '../../models';
import {
  AXIS_BUTTON_TYPES,
  ButtonDef,
  DEFAULT_AXIS_DEADZONE,
  DEFAULT_AXIS_EXPO,
  defaultAxisDeadzones,
  defaultAxisExpos,
} from '../../models';

interface AxisTuningRow {
  type: ButtonType;
  label: string;
  deadzone: number;
  expo: number;
}

@Component({
  selector: 'app-gamepad-deadzone-config',
  templateUrl: './gamepad-deadzone-config.component.html',
  styleUrls: ['../shared-styles.scss', './gamepad-deadzone-config.component.scss'],
})
export class GamepadDeadzoneConfigComponent implements OnChanges {
  /** Per-axis deadzone map, keyed by stick-axis ButtonType. */
  @Input()
  axisDeadzones: { [buttonType: number]: number } = defaultAxisDeadzones();

  /** Per-axis expo (response-curve) map, keyed by stick-axis ButtonType. */
  @Input()
  axisExpos: { [buttonType: number]: number } = defaultAxisExpos();

  @Output()
  axisDeadzonesChange = new EventEmitter<{ [buttonType: number]: number }>();

  @Output()
  axisExposChange = new EventEmitter<{ [buttonType: number]: number }>();

  readonly deadzoneMin = 0;
  readonly deadzoneMax = 0.95;
  readonly deadzoneStep = 0.01;
  readonly defaultDeadzone = DEFAULT_AXIS_DEADZONE;

  readonly expoMin = 0;
  readonly expoMax = 1;
  readonly expoStep = 0.05;
  readonly defaultExpo = DEFAULT_AXIS_EXPO;

  rows: AxisTuningRow[] = [];

  trackByType(_index: number, row: AxisTuningRow): ButtonType {
    return row.type;
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['axisDeadzones'] || changes['axisExpos']) {
      this.buildRows();
    }
  }

  onDeadzoneChange(type: ButtonType, value: number) {
    const clamped = this.clamp(value, this.deadzoneMin, this.deadzoneMax, this.defaultDeadzone);
    const updated = { ...defaultAxisDeadzones(), ...this.axisDeadzones, [type]: clamped };

    this.axisDeadzones = updated;
    this.buildRows();
    this.axisDeadzonesChange.next(updated);
  }

  onExpoChange(type: ButtonType, value: number) {
    const clamped = this.clamp(value, this.expoMin, this.expoMax, this.defaultExpo);
    const updated = { ...defaultAxisExpos(), ...this.axisExpos, [type]: clamped };

    this.axisExpos = updated;
    this.buildRows();
    this.axisExposChange.next(updated);
  }

  private buildRows() {
    const deadzones = { ...defaultAxisDeadzones(), ...(this.axisDeadzones || {}) };
    const expos = { ...defaultAxisExpos(), ...(this.axisExpos || {}) };

    this.rows = AXIS_BUTTON_TYPES.map((type) => ({
      type,
      label: ButtonDef.defaultDef(type).text,
      deadzone: deadzones[type],
      expo: expos[type],
    }));
  }

  private clamp(value: number, min: number, max: number, fallback: number): number {
    if (value == null || isNaN(value)) {
      return fallback;
    }
    return Math.min(max, Math.max(min, value));
  }
}
